<template>
  <button
    v-if="!isOpen"
    @click="openAssistant"
    class="ai-assistant-button"
    :class="{ 'struggling': isStruggling, 'dark-mode': isDarkMode }"
    title="AI Assistant (Ctrl+K)"
  >
    <div class="button-icon">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
        <path d="M12 6v6l4 2"/>
      </svg>
    </div>
    
    <span v-if="isStruggling" class="notification-badge">!</span>
    
    <div class="button-tooltip">
      <span v-if="isStruggling">Need help? I'm here!</span>
      <span v-else>AI Assistant</span>
      <span class="tooltip-shortcut">Ctrl+K</span>
    </div>
  </button>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'AIAssistantButton',

  computed: {
    ...mapGetters('aiAssistant', [
      'isOpen',
      'isStruggling',
    ]),

    isDarkMode() {
      return this.$store.state.darkMode;
    },
  },

  methods: {
    ...mapActions('aiAssistant', ['open']),

    openAssistant() {
      this.open();
    },
  },
};
</script>

<style scoped lang="scss">
.ai-assistant-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 50%;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 24px rgba(102, 126, 234, 0.5);

    .button-tooltip {
      opacity: 1;
      transform: translateX(-8px);
    }
  }

  &:active {
    transform: scale(0.95);
  }

  &.struggling {
    animation: pulse 2s infinite, shake 0.5s ease-in-out;
  }

  &.dark-mode {
    box-shadow: 0 4px 16px rgba(139, 154, 237, 0.3);

    &:hover {
      box-shadow: 0 6px 24px rgba(139, 154, 237, 0.4);
    }
  }
}

.button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  animation: float 3s ease-in-out infinite;
}

.notification-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #ff4444;
  color: #ffffff;
  border-radius: 50%;
  border: 3px solid #ffffff;
  font-size: 14px;
  font-weight: bold;
  animation: bounce 2s infinite;
}

.button-tooltip {
  position: absolute;
  right: 100%;
  margin-right: 12px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.85);
  color: #ffffff;
  border-radius: 8px;
  font-size: 13px;
  white-space: nowrap;
  opacity: 0;
  transform: translateX(0);
  transition: all 0.3s ease;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &::after {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid rgba(0, 0, 0, 0.85);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
  }

  .tooltip-shortcut {
    font-size: 11px;
    opacity: 0.7;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4), 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  50% {
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4), 0 0 0 10px rgba(102, 126, 234, 0);
  }
}

@keyframes bounce {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-5px);
  }
  40% {
    transform: translateX(5px);
  }
  60% {
    transform: translateX(-5px);
  }
  80% {
    transform: translateX(5px);
  }
}

// Responsive
@media (max-width: 768px) {
  .ai-assistant-button {
    bottom: 16px;
    right: 16px;
  }

  .button-tooltip {
    display: none;
  }
}
</style>
